import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';
import { DayButton } from './DayButton';
import { monthGrid, monthTitle, WEEKDAY_LABELS } from './helpers';
import type { DayState, ISODate } from './types';

type Props = {
  year: number;
  monthIdx: number;
  onPrev?: () => void;
  onNext?: () => void;
  onSelect: (iso: ISODate) => void;
  dayProps: (iso: ISODate) => DayState;
};

export function CalendarMonth({ year, monthIdx, onPrev, onNext, onSelect, dayProps }: Props) {
  const cells = monthGrid(year, monthIdx);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-9 items-center gap-2">
        <NavSlot icon="prev" onClick={onPrev} />
        <span className="flex h-9 flex-1 items-center justify-center rounded-md bg-control text-sm text-control-foreground">
          {monthTitle(year, monthIdx)}
        </span>
        <NavSlot icon="next" onClick={onNext} />
      </div>
      <div className="grid grid-cols-7 gap-x-1">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="flex size-8 items-center justify-center text-sm text-text-tertiary"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-x-1 gap-y-1">
        {cells.map((iso, i) =>
          iso ? (
            <DayButton key={iso} iso={iso} {...dayProps(iso)} onSelect={onSelect} />
          ) : (
            <div key={`pad-${i}`} className="size-8" />
          ),
        )}
      </div>
    </div>
  );
}

function NavSlot({ icon, onClick }: { icon: 'prev' | 'next'; onClick?: () => void }) {
  if (!onClick) return null;
  const Icon = icon === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={icon === 'prev' ? 'Previous month' : 'Next month'}
      className={cn(
        'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md bg-control text-control-foreground',
        'transition-colors hover:bg-control-hover',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
